/**
 * Deck normalization + validation.
 *
 * Pure module: operates on already-parsed JSON (no filesystem access). The
 * Node-only file loader lives in deckLoader.ts so this module stays safe to
 * import from any environment (including a bundler/browser type-check).
 */
/** Canonical blank token used for display/normalization. */
export const CANONICAL_BLANK = '____';
const BLANK_RUN = /_{2,}/g;
/** Normalize any run of 2+ underscores to the canonical `____` blank. */
export function normalizeBlankText(text) {
    return text.replace(BLANK_RUN, CANONICAL_BLANK).replace(/[ \t]+/g, ' ').trim();
}
/** Count blank placeholders (runs of 2+ underscores) in a string. */
export function countBlanks(text) {
    const matches = text.match(BLANK_RUN);
    return matches ? matches.length : 0;
}
function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function asString(value) {
    return typeof value === 'string' ? value : null;
}
function needsReview(raw) {
    const review = raw['review'];
    return isObject(review) && review['needsReview'] === true;
}
/**
 * Validate and normalize a raw parsed deck object into a typed `Deck`.
 *
 * - Honors black-card `pick` exactly as authored (1–3), regardless of blank count.
 * - Normalizes blank placeholders to `____`.
 * - Skips needsReview cards unless `includeNeedsReview` is set.
 * - Duplicate text → warning; duplicate id → error.
 * - Preserves all source metadata.
 */
export function validateAndNormalizeDeck(raw, options = {}) {
    const errors = [];
    const warnings = [];
    let skipped = 0;
    if (!isObject(raw)) {
        return { ok: false, errors: ['Deck is not a JSON object.'], warnings, skipped };
    }
    const id = asString(raw['id']);
    const name = asString(raw['name']);
    if (!id)
        errors.push('Deck is missing a string "id".');
    if (!name)
        warnings.push('Deck is missing a "name".');
    const rawSource = raw['source'];
    if (!isObject(rawSource) || !asString(rawSource['title'])) {
        errors.push('Deck is missing "source.title".');
    }
    const rawBlack = raw['blackCards'];
    const rawWhite = raw['whiteCards'];
    if (!Array.isArray(rawBlack))
        errors.push('"blackCards" must be an array.');
    if (!Array.isArray(rawWhite))
        errors.push('"whiteCards" must be an array.');
    if (errors.length > 0) {
        return { ok: false, errors, warnings, skipped };
    }
    const seenIds = new Set();
    const blackCards = [];
    const whiteCards = [];
    const seenBlackText = new Map();
    const seenWhiteText = new Map();
    for (const item of rawBlack) {
        if (!isObject(item)) {
            errors.push('A black card is not an object.');
            continue;
        }
        if (needsReview(item) && !options.includeNeedsReview) {
            skipped += 1;
            continue;
        }
        const cardId = asString(item['id']);
        const text = asString(item['text']);
        const source = asString(item['source']);
        const pickRaw = item['pick'];
        if (!cardId) {
            errors.push('A black card is missing "id".');
            continue;
        }
        if (seenIds.has(cardId)) {
            errors.push(`Duplicate card id: ${cardId}`);
            continue;
        }
        if (!text) {
            errors.push(`Black card ${cardId} is missing "text".`);
            continue;
        }
        if (!source) {
            errors.push(`Black card ${cardId} is missing "source".`);
            continue;
        }
        let pick = typeof pickRaw === 'number' && Number.isInteger(pickRaw) ? pickRaw : 1;
        if (pick < 1 || pick > 3) {
            warnings.push(`Black card ${cardId} has unusual pick=${pick}; clamping to 1..3.`);
            pick = Math.min(3, Math.max(1, pick));
        }
        const normalizedText = normalizeBlankText(text);
        const textKey = normalizedText.toLowerCase();
        const dupOf = seenBlackText.get(textKey);
        if (dupOf)
            warnings.push(`Duplicate black text "${normalizedText}" (${dupOf} & ${cardId}).`);
        else
            seenBlackText.set(textKey, cardId);
        seenIds.add(cardId);
        const card = {
            id: cardId,
            type: 'black',
            text: normalizedText,
            pick,
            source,
        };
        if (isObject(item['review']))
            card.review = item['review'];
        blackCards.push(card);
    }
    for (const item of rawWhite) {
        if (!isObject(item)) {
            errors.push('A white card is not an object.');
            continue;
        }
        if (needsReview(item) && !options.includeNeedsReview) {
            skipped += 1;
            continue;
        }
        const cardId = asString(item['id']);
        const text = asString(item['text']);
        const source = asString(item['source']);
        if (!cardId) {
            errors.push('A white card is missing "id".');
            continue;
        }
        if (seenIds.has(cardId)) {
            errors.push(`Duplicate card id: ${cardId}`);
            continue;
        }
        if (!text) {
            errors.push(`White card ${cardId} is missing "text".`);
            continue;
        }
        if (!source) {
            errors.push(`White card ${cardId} is missing "source".`);
            continue;
        }
        const normalizedText = normalizeBlankText(text);
        const textKey = normalizedText.toLowerCase();
        const dupOf = seenWhiteText.get(textKey);
        if (dupOf)
            warnings.push(`Duplicate white text "${normalizedText}" (${dupOf} & ${cardId}).`);
        else
            seenWhiteText.set(textKey, cardId);
        seenIds.add(cardId);
        const card = {
            id: cardId,
            type: 'white',
            text: normalizedText,
            source,
        };
        if (isObject(item['review']))
            card.review = item['review'];
        whiteCards.push(card);
    }
    if (blackCards.length === 0)
        errors.push('Deck has no usable black cards.');
    if (whiteCards.length === 0)
        errors.push('Deck has no usable white cards.');
    if (errors.length > 0) {
        return { ok: false, errors, warnings, skipped };
    }
    const source = rawSource;
    const deck = {
        id: id,
        name: (name ?? id),
        source,
        blackCards,
        whiteCards,
    };
    return { ok: true, deck, errors, warnings, skipped };
}
//# sourceMappingURL=deck.js.map